import { useCallback, useEffect, useState } from 'react';

interface ConversationProcessingLease {
  ownerId: string;
  expiresAt: number;
}

interface UseConversationProcessingLockReturn {
  isLockedByAnotherTab: boolean;
  acquireLock: (targetConversationId?: string | null) => Promise<(() => void) | null>;
}

const LEASE_DURATION_MS = 6 * 60_000;
const LEASE_HEARTBEAT_MS = 15_000;
const TAB_OWNER_ID = `${Date.now()}-${Math.random()}`;

export const CHATBOT_PROCESSING_LOCK_PREFIX = 'ces-chatbot-processing-lock:';

function getLockKey(conversationId: string): string {
  return `${CHATBOT_PROCESSING_LOCK_PREFIX}${conversationId}`;
}

function isConversationProcessingLease(value: unknown): value is ConversationProcessingLease {
  return (
    typeof value === 'object' &&
    value !== null &&
    'ownerId' in value &&
    'expiresAt' in value &&
    typeof value.ownerId === 'string' &&
    typeof value.expiresAt === 'number'
  );
}

function readLease(key: string): ConversationProcessingLease | null {
  try {
    const serializedLease = localStorage.getItem(key);
    if (!serializedLease) return null;

    const lease: unknown = JSON.parse(serializedLease);
    return isConversationProcessingLease(lease) ? lease : null;
  } catch {
    return null;
  }
}

function writeLease(key: string, lease: ConversationProcessingLease): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(lease));
    return true;
  } catch {
    return false;
  }
}

function removeOwnedLease(key: string): void {
  try {
    if (readLease(key)?.ownerId === TAB_OWNER_ID) {
      localStorage.removeItem(key);
    }
  } catch {
    return;
  }
}

function createLease(): ConversationProcessingLease {
  return {
    ownerId: TAB_OWNER_ID,
    expiresAt: Date.now() + LEASE_DURATION_MS,
  };
}

function startLeaseHeartbeat(key: string): () => void {
  const heartbeat = window.setInterval(() => {
    if (readLease(key)?.ownerId !== TAB_OWNER_ID) {
      window.clearInterval(heartbeat);
      return;
    }

    writeLease(key, createLease());
  }, LEASE_HEARTBEAT_MS);

  return () => {
    window.clearInterval(heartbeat);
  };
}

function claimLease(key: string): (() => void) | null {
  if (!writeLease(key, createLease())) return null;
  if (readLease(key)?.ownerId !== TAB_OWNER_ID) return null;

  const stopLeaseHeartbeat = startLeaseHeartbeat(key);
  return () => {
    stopLeaseHeartbeat();
    removeOwnedLease(key);
  };
}

function acquireStorageLease(key: string): (() => void) | null {
  const currentLease = readLease(key);
  if (currentLease && currentLease.expiresAt > Date.now()) return null;

  return claimLease(key);
}

function acquireBrowserLock(key: string): Promise<(() => void) | null> {
  return new Promise(resolve => {
    let completed = false;
    const complete = (releaseLock: (() => void) | null): void => {
      if (!completed) {
        completed = true;
        resolve(releaseLock);
      }
    };

    void navigator.locks
      .request(key, { ifAvailable: true }, lock => {
        if (!lock) {
          complete(null);
          return;
        }

        const releaseLease = claimLease(key);
        if (!releaseLease) {
          complete(null);
          return;
        }

        let releaseBrowserLock = (): void => undefined;
        const holdLock = new Promise<void>(resolveHoldLock => {
          releaseBrowserLock = resolveHoldLock;
        });
        complete(() => {
          releaseLease();
          releaseBrowserLock();
        });

        return holdLock;
      })
      .catch(() => complete(acquireStorageLease(key)));
  });
}

function isLockedByAnotherTab(conversationId: string | null): boolean {
  if (!conversationId) return false;

  const lease = readLease(getLockKey(conversationId));
  return Boolean(lease && lease.ownerId !== TAB_OWNER_ID && lease.expiresAt > Date.now());
}

const useConversationProcessingLock = (conversationId: string | null): UseConversationProcessingLockReturn => {
  const [lockedByAnotherTab, setLockedByAnotherTab] = useState(() => isLockedByAnotherTab(conversationId));

  useEffect(() => {
    const updateLockState = (): void => {
      setLockedByAnotherTab(isLockedByAnotherTab(conversationId));
    };
    const handleStorage = (event: StorageEvent): void => {
      if (conversationId && event.key === getLockKey(conversationId)) {
        updateLockState();
      }
    };

    updateLockState();
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [conversationId]);

  useEffect(() => {
    if (!lockedByAnotherTab) return undefined;

    const refreshInterval = window.setInterval(() => {
      setLockedByAnotherTab(isLockedByAnotherTab(conversationId));
    }, LEASE_HEARTBEAT_MS);

    return () => {
      window.clearInterval(refreshInterval);
    };
  }, [conversationId, lockedByAnotherTab]);

  const acquireLock = useCallback(
    async (targetConversationId = conversationId): Promise<(() => void) | null> => {
      if (!targetConversationId) return () => undefined;

      const key = getLockKey(targetConversationId);
      const currentLease = readLease(key);
      if (currentLease && currentLease.expiresAt > Date.now() && currentLease.ownerId !== TAB_OWNER_ID) {
        return null;
      }

      if (navigator.locks) return acquireBrowserLock(key);

      return acquireStorageLease(key);
    },
    [conversationId],
  );

  return { isLockedByAnotherTab: lockedByAnotherTab, acquireLock };
};

export default useConversationProcessingLock;
